<template>
  <div class="main-user-table-wrapper">
    <v-data-table
      :headers="headers"
      :items="items"
      :header-props="{ sortIcon: 'bhi-menu-down' }"
      :items-per-page="itemsPerPage"
      :hide-default-footer="hideDefaultFooter"
      :server-items-length="serverItemsLength"
      :page="page"
      :footer-props="footerProps"
      mobile-breakpoint="1"
      :disable-pagination="items.length == 0"
      class="elevation-0 main-user-table"
      @pagination="onPagination"
      @update:sort-desc="onSortDesc"
      @update:sort-by="onSortBy"
    >
      <template v-slot:body="{ items }">
        <tbody>
          <template v-for="user in items">
            <component
              :is="rowComponent"
              :key="user.id"
              :user="user"
              v-bind="$attrs"
              v-on="$listeners"
              :selected-all="usersAllSelected"
              :is-admin="isAdmin"
              @to-user="$emit('to-user', $event)"
            />
            <tr class="main-user-table-empty" :key="user.id + '-empty'">
              <td class="main-user-table-empty">&nbsp;</td>
            </tr>
          </template>
        </tbody>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import BhUserTableRow from '@/components/bhUsers/BhUserTableRow.vue'
import PracticeTableRow from '@/components/practices/PracticeTableRow.vue'
import PracticeTableRowWithSelect from '@/components/practices/PracticeTableRowWithSelect.vue'
import PracticeUsersTableRow from '@/components/practices/PracticeUsersTableRow.vue'
import PracticeUserPatientsTableRow from '@/components/practices/PracticeUserPatientsTableRow.vue'
import PracticeUserPatientWithSelectTableRow from '@/components/practices/PracticeUserPatientWithSelectTableRow.vue'
import PracticePatientsTableRow from '@/components/practices/PracticePatientsTableRow.vue'
import PracticePatientsTableRowWithSelect from '@/components/practices/PracticePatientsTableRowWithSelect.vue'
import BhUserPatientWithSelectTableRow from '@/components/bhUsers/BhUserPatientWithSelectTableRow.vue'
import PatientTableRow from '@/components/patients/PatientTableRow'
import ParticipantsListTableRow from '@/components/patients/ParticipantsListTableRow'
import ParticipantSelectableTableRow from '@/components/patients/ParticipantSelectableTableRow'
import BhPatientTableRow from '@/components/patients/BhPatientTableRow'
import TemplatesTableRow from '@/components/timedRelease/TemplatesTableRow'
import ChoosePracticeTableRow from '@/components/timedRelease/ChoosePracticeTableRow'
import TemplatesSelectTableRow from '@/components/timedRelease/TemplatesSelectTableRow'
import ExerciseDefinitionTableRow from '@/components/exercises/ExerciseDefinitionTableRow'
import ParticipantSelectorTableRow from '@/components/patients/ParticipantSelectorTableRow'
import ExerciseDefinitionSelectableTableRow from '@/components/exercises/ExerciseDefinitionSelectableTableRow'
import SequenceDefinitionTableRow from '@/components/exercises/SequenceDefinitionTableRow'
import SequenceAssignParticipantTableRow from '@/components/exercises/SequenceAssignParticipantTableRow'
import SequenceAssignedParticipantTableRow from '@/components/exercises/SequenceAssignedParticipantTableRow'
import ScreenersTableRow from '@/components/screeners/ScreenersTableRow'
import ScreenersDetailedTableRow from '@/components/screeners/ScreenersDetailedTableRow'
import ParticipantCoursesTableRow from '@/components/courses/ParticipantCoursesTableRow'
import ParticipantCourseStepsTableRow from '@/components/courses/ParticipantCourseStepsTableRow'
import PatientScreenersTableRow from '@/components/screeners/PatientScreenersTableRow'
import ParticipantSwapsTableRow from '@/components/swaps/ParticipantSwapsTableRow'

import { debounce } from '@/helpers/debounce'

export default {
  name: 'MainUserTable',
  components: {
    PracticePatientsTableRow,
    PracticePatientsTableRowWithSelect,
    BhUserTableRow,
    PracticeTableRow,
    PracticeTableRowWithSelect,
    PracticeUsersTableRow,
    PracticeUserPatientsTableRow,
    PracticeUserPatientWithSelectTableRow,
    PatientTableRow,
    ParticipantsListTableRow,
    ParticipantSelectableTableRow,
    BhPatientTableRow,
    TemplatesTableRow,
    ChoosePracticeTableRow,
    TemplatesSelectTableRow,
    BhUserPatientWithSelectTableRow,
    ExerciseDefinitionTableRow,
    ExerciseDefinitionSelectableTableRow,
    SequenceDefinitionTableRow,
    SequenceAssignParticipantTableRow,
    SequenceAssignedParticipantTableRow,
    ScreenersTableRow,
    ScreenersDetailedTableRow,
    PatientScreenersTableRow,
    ParticipantSelectorTableRow,
    ParticipantCoursesTableRow,
    ParticipantCourseStepsTableRow,
    ParticipantSwapsTableRow,
  },
  props: {
    headers: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    rowComponent: {
      type: String,
      default: 'bh-user-table-row',
    },
    itemsPerPage: {
      type: Number,
      default: 10,
    },
    showAllItems: {
      type: Boolean,
      default: false,
    },
    hideDefaultFooter: {
      type: Boolean,
      default: false,
    },
    serverItemsLength: {
      type: Number,
      default: -1,
    },
    page: {
      type: Number,
      default: 1,
    },
    usersAllSelected: {
      type: Boolean,
      default: false,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    let itemsPerPageOptions = [10, 25, 50, 100]
    if (this.$props.showAllItems) {
      itemsPerPageOptions = [-1, ...itemsPerPageOptions]
    }
    return {
      paramsStore: null,
      footerProps: { 'items-per-page-options': itemsPerPageOptions },
      init: true,
    }
  },
  methods: {
    onSortBy(data) {
      this.paramsHasChanged({ sortBy: !data[0] ? null : data })
    },
    onSortDesc(data) {
      const sortDirection =
        !data[0] && data[0] !== false
          ? null
          : data.map(item => (item ? 'desc' : 'asc'))
      this.paramsHasChanged({ sortDirection })
    },
    onPagination({ page, itemsPerPage }) {
      // @todo event on mounted - so far I don't know why this is called - bug in v-data-table?
      if (this.init && page === 1 && itemsPerPage === 10) {
        return
      }
      this.init = false
      this.paramsHasChanged({ page, pageSize: itemsPerPage })
    },
    paramsHasChanged(data) {
      this.paramsStore = this.paramsStore
        ? { ...this.paramsStore, ...data }
        : data
      debounce(
        () => {
          this.$emit('action:get-data', { ...this.paramsStore })
          this.paramsStore = null
        },
        100,
        'MainUserTable',
      )
    },
  },
}
</script>

<style lang="scss">
.main-user-table-wrapper {
  table {
    border-spacing: 0;
  }
  .main-user-table {
    background-color: transparent !important;

    thead > tr {
      border-bottom: 0 !important;
      vertical-align: bottom;
    }

    th {
      white-space: nowrap !important;
      color: $bh-blue !important;
      text-transform: uppercase;
      font-size: 0.715rem !important;
      line-height: 2.4em;
      height: auto !important;
      padding: 0 0.5 * $gutter !important;
      border-bottom: 0 !important;
      letter-spacing: 1px;
      font-weight: 700;

      span {
        vertical-align: middle;
        font-weight: 700;
      }

      .v-data-table-header__icon {
        color: $bh-blue--3 !important;
        opacity: 1;
        transform: rotate(-90deg);
        margin-left: 0.25 * $gutter;
      }

      &:hover,
      &.sortable.active {
        .v-data-table-header__icon {
          color: $bh-blue !important;
          opacity: 1;
        }
      }

      &:hover .v-data-table-header__icon {
        transform: rotate(-90deg);
      }

      &.sortable.active:hover .v-data-table-header__icon {
        transform: rotate(0deg);
      }

      &.sortable.active.desc:hover .v-data-table-header__icon {
        transform: rotate(-180deg);
      }
    }

    table {
      min-width: 960px;
    }

    &-empty {
      background: transparent !important;
      height: 0 !important;
      border-bottom: 0 !important;
      font-size: 0.381rem !important;
    }

    .v-data-footer {
      justify-content: center;
      border-top: 0 !important;
      &__select {
        margin-left: 0;
      }
    }
  }
}
</style>
